'use client';

import { useState, useEffect, useCallback } from 'react';
import { OrganisationHeader } from '@/components/organisation/OrganisationHeader';
import { PublicJobDetails } from '@/components/public/PublicJobDetails';
import { Button } from '@/components/ui/button';
import { PoweredByFooter } from '@/components/layout/PoweredByFooter';
import type { Organisation } from '@/types/organisation';
import type { Job } from '@/types/jobs';
import { publicApi } from '@/api/public';

interface ClientOnlyJobContentProps {
  orgId: string;
  jobId: string;
}

// This component handles client-side functionality and data fetching
export default function ClientOnlyJobContent({ 
  orgId, 
  jobId 
}: ClientOnlyJobContentProps) {
  const [hasMounted, setHasMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handle hydration completion
  useEffect(() => {
    try {
      setHasMounted(true);
    } catch (error) {
      console.error('Error in ClientOnlyJobContent useEffect:', error);
      setHasError(true);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch job and organization data concurrently
        const [jobResult, orgResult] = await Promise.allSettled([
          publicApi.getJob(orgId, jobId),
          publicApi.getOrganisation(orgId)
        ]);

        // Process job data
        if (jobResult.status === 'fulfilled') {
          const jobData = jobResult.value.job;
          if (!jobData || !jobData.id || !jobData.title) {
            throw new Error('Invalid job data received');
          }
          setJob(jobData);
        } else {
          console.error('Error fetching job:', jobResult.reason);
          setError('Job not found');
          return;
        }

        // Process organization data
        if (orgResult.status === 'fulfilled') {
          const orgData = orgResult.value.organisation;
          if (!orgData || !orgData.id || !orgData.name) {
            throw new Error('Invalid organization data received');
          }
          setOrganisation(orgData);
        } else {
          console.error('Error fetching organization:', orgResult.reason);
          setError('Organization not found');
          return;
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Something went wrong');
      } finally {
        setIsLoading(false);
      }
    }

    if (hasMounted) {
      fetchData();
    }
  }, [orgId, jobId, hasMounted]);

  // Handle apply button click
  const handleApply = useCallback(() => {
    try {
      const candidateAppUrl = process.env.NEXT_CANDIDATE_APP_URL;
      if (!candidateAppUrl) {
        console.error('NEXT_CANDIDATE_APP_URL is not defined');
        return;
      }
      window.open(`${candidateAppUrl}?jobId=${jobId}`, '_blank');
    } catch (error) {
      console.error('Error opening apply link:', error);
    }
  }, [jobId]);

  // Show error state if something went wrong
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            We encountered an error while loading this page. Please refresh and try again.
          </p>
          <Button 
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading || !hasMounted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto p-6">
          <div className="animate-pulse space-y-6">
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-6 bg-gray-200 rounded w-24"></div>
              <div className="h-6 bg-gray-200 rounded w-32"></div>
              <div className="h-6 bg-gray-200 rounded w-40"></div>
            </div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state for missing data
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-foreground">
            {error === 'Job not found' ? 'Job Not Found' : 'Organization Not Found'}
          </h1>
          <p className="text-muted-foreground">
            {error === 'Job not found' 
              ? "The job you're looking for doesn't exist or has been removed."
              : "The organization you're looking for doesn't exist or has been removed."
            }
          </p>
          <div className="mt-6">
            <a
              href={error === 'Job not found' ? `/careers/${orgId}` : '/'}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4"
            >
              {error === 'Job not found' ? 'Back to Jobs' : 'Go Home'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show main content
  if (!job || !organisation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-6 max-w-lg mx-auto">
          <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            We encountered an unexpected error while loading this job. Please try again later.
          </p>
          <div className="mt-6 space-x-4">
            <a
              href={`/careers/${orgId}`}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-primary text-primary-foreground hover:bg-primary/90 h-10 py-2 px-4"
            >
              Back to Jobs
            </a>
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 py-2 px-4"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen relative pb-12 sm:pb-0">
      <OrganisationHeader organisation={organisation} />
      <div className="flex-grow">
        <PublicJobDetails job={job} organisation={organisation} onApply={handleApply} />
      </div>

      {/* Extra padding at the bottom to prevent content from being hidden behind the buttons */}
      {hasMounted && <div className="h-24 sm:hidden" />}
      
      {/* Mobile sticky apply button - CSS-only responsive design */}
      {hasMounted && (
        <div className="fixed bottom-10 left-0 right-0 px-4 py-3 bg-card-background border-t shadow-lg z-10 sm:hidden">
          {job.status === 'ACTIVE' ? (
            <Button 
              variant="default" 
              className="w-full py-6 text-base font-semibold shadow-sm" 
              onClick={handleApply}
            >
              Apply Now
            </Button>
          ) : (
            <Button 
              variant="outline" 
              className="w-full py-6 text-base font-semibold shadow-sm" 
              disabled
            >
              Application Closed
            </Button>
          )}
        </div>
      )}
      
      {/* Footer with Fomogo branding - now fixed at the bottom */}
      <div className="fixed bottom-0 left-0 right-0 w-full z-5 bg-background">
        <PoweredByFooter />
      </div>
    </div>
  );
} 